import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  SECTORS,
  SECTOR_LABELS,
  SHIPPING_TYPES,
  SectorEnum,
  ShippingTypeEnum,
} from "../constants";
import { SECTOR_EMAILS_LABELS } from "../messages";
import { SectorRecipient, SectorRecipientInput } from "../types";
import { RecipientFormSchema, RecipientFormValues } from "./validate-form";

interface RecipientFormModalProps {
  recipient?: SectorRecipient;
  onSubmit: (input: SectorRecipientInput) => void;
}

export function RecipientFormModal({
  recipient,
  onSubmit,
}: RecipientFormModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const isEdit: boolean = !!recipient;

  const form = useForm<RecipientFormValues>({
    resolver: zodResolver(RecipientFormSchema),
    defaultValues: {
      sector: recipient?.sector,
      name: recipient?.name ?? "",
      email: recipient?.email ?? "",
      shipping_types: recipient?.shipping_types ?? [],
    },
  });

  const selectedTypes: ShippingTypeEnum[] = form.watch("shipping_types") ?? [];

  const toggleType = (value: ShippingTypeEnum): void => {
    const next: ShippingTypeEnum[] = selectedTypes.includes(value)
      ? selectedTypes.filter((type: ShippingTypeEnum) => type !== value)
      : [...selectedTypes, value];

    form.setValue("shipping_types", next, { shouldValidate: true });
  };

  const submit = (values: RecipientFormValues): void => {
    onSubmit({
      sector: values.sector,
      name: values.name?.trim() ?? "",
      email: values.email.trim(),
      shipping_types: values.shipping_types,
    });
    handleModal({ isOpen: false });
    form.reset();
  };

  return (
    <div className="w-full max-w-xl rounded-lg bg-white">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="w-full space-y-5"
        >
          <FormField
            control={form.control}
            name="sector"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="sector">
                  {SECTOR_EMAILS_LABELS.FIELD_SECTOR}{" "}
                  {SECTOR_EMAILS_LABELS.REQUIRED_MARK}
                </Label>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger
                      id="sector"
                      className={`mt-1 ${
                        form.formState.errors.sector ? "border-red-500" : ""
                      }`}
                    >
                      <SelectValue
                        placeholder={SECTOR_EMAILS_LABELS.SECTOR_PLACEHOLDER}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {SECTORS.map((sector: SectorEnum) => (
                      <SelectItem key={sector} value={sector}>
                        {SECTOR_LABELS[sector]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="name">
                  {SECTOR_EMAILS_LABELS.FIELD_NAME}
                </Label>
                <FormControl>
                  <Input {...field} id="name" className="mt-1" />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {SECTOR_EMAILS_LABELS.FIELD_NAME_HINT}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="email">
                  {SECTOR_EMAILS_LABELS.FIELD_EMAIL}{" "}
                  {SECTOR_EMAILS_LABELS.REQUIRED_MARK}
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    id="email"
                    type="email"
                    className={`mt-1 ${
                      form.formState.errors.email ? "border-red-500" : ""
                    }`}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="shipping_types"
            render={() => (
              <FormItem>
                <Label>
                  {SECTOR_EMAILS_LABELS.FIELD_SHIPPING_TYPES}{" "}
                  {SECTOR_EMAILS_LABELS.REQUIRED_MARK}
                </Label>
                <div className="mt-2 space-y-3">
                  {SHIPPING_TYPES.map((type) => (
                    <div key={type.value} className="flex items-start gap-3">
                      <Checkbox
                        id={type.value}
                        checked={selectedTypes.includes(type.value)}
                        onCheckedChange={() => toggleType(type.value)}
                        className="mt-0.5"
                      />
                      <label htmlFor={type.value} className="cursor-pointer">
                        <span className="block text-sm font-medium text-gray-900">
                          {type.label}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {type.microcopy}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <p className="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
            {SECTOR_EMAILS_LABELS.FISCAL_PERIOD_NOTE}
          </p>

          <p
            className={`rounded-md p-3 text-xs ${
              isEdit
                ? "bg-[#613F4F]/5 text-[#613F4F]"
                : "bg-[#F6D040]/10 text-[#613F4F]"
            }`}
          >
            {SECTOR_EMAILS_LABELS.AUDIT_NOTE}
          </p>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleModal({ isOpen: false })}
            >
              {SECTOR_EMAILS_LABELS.CANCEL}
            </Button>
            <Button type="submit">{SECTOR_EMAILS_LABELS.SAVE}</Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
